import React, { useMemo, useState } from 'react';
import { Typography } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { handleContactProvider } from '../../utils/helpers';
import AuthModal from '../common/AuthModal/AuthModal';
import CompactBusinessRatingLine from '../common/CompactBusinessRatingLine/CompactBusinessRatingLine';
import { RatingsStack } from '../common/CompactBusinessRatingLine/CompactBusinessRatingLine.styled';
import {
  StyledCard,
  StarContainer,
  StyledCardContent,
  BusinessPhoto,
  BusinessHeader,
  BusinessInfo,
  BusinessName,
  LocationInfo,
  EmailInfo,
  RatingRow,
  StyledDivider,
  ServicesTitle,
  ServicesList,
  ServiceItem,
  ContactActionsRow,
  ContactBox,
  CallBox,
} from './ProviderCard.styled';

/* ──────────────────────────────────────────────
   Trade label map — aligned with ServiceType / Cosmos short_name
   ────────────────────────────────────────────── */
const SERVICE_TYPE_LABELS = {
  electrical: 'Electrical',
  hvac: 'HVAC',
  landscaping: 'Landscaping & Lawn Care',
  plumbing: 'Plumbing',
  other: 'Custom Services',
};

/* Format "8592400034" → "(859) 240-0034" */
const formatPhone = (raw = '') => {
  const digits = String(raw).replace(/\D/g, '');
  if (digits.length !== 10) return raw;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

/** Digits-only href for tel: (US 10-digit → +1…) */
const getTelHref = (raw) => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `tel:+${digits}`;
  return `tel:+${digits}`;
};

const ProviderCard = ({
  provider,
  locationDisplay,
  isSaved = false,
  onToggleSaved,
  onRemove,
  onClick,
}) => {
  const { user } = useAuth();
  const [showAuthDialog, setShowAuthDialog] = useState(false);

  /* Derive the list of trades the business is enrolled in.
     Pulls top-level Services[].ServiceType, dedupes, maps to display label. */
  const tradeLabels = useMemo(() => {
    const services = provider.Services ?? [];
    const uniqueTypes = [
      ...new Set(services.map((s) => s.ServiceType).filter(Boolean)),
    ];
    return uniqueTypes
      .map((type) => SERVICE_TYPE_LABELS[type] ?? type)
      .sort();
  }, [provider.Services]);

  /* Derive a clean location string. Caller can override via prop. */
  const resolvedLocation = useMemo(() => {
    if (locationDisplay) return locationDisplay;
    const city = provider.BusinessAddress?.City;
    const state = provider.BusinessAddress?.State;
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return 'Northern Kentucky';
  }, [locationDisplay, provider.BusinessAddress]);

  const formattedPhone = useMemo(
    () =>
      provider.BusinessPhoneNumber
        ? formatPhone(provider.BusinessPhoneNumber)
        : null,
    [provider.BusinessPhoneNumber],
  );

  const telHref = useMemo(
    () => getTelHref(provider.BusinessPhoneNumber),
    [provider.BusinessPhoneNumber],
  );

  const reviewCountNum = Number(provider.ReviewCount ?? 0);
  const hasReviews =
    reviewCountNum > 0 &&
    provider.AverageRating != null &&
    !Number.isNaN(Number(provider.AverageRating));

  const googleReview = provider.GoogleReview;
  const googleReviewCount = Number(googleReview?.ReviewCount ?? 0);
  const hasGoogleReview =
    googleReview?.Rating != null &&
    !Number.isNaN(Number(googleReview.Rating)) &&
    googleReviewCount > 0;

  const handleContactClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleContactProvider(provider.BusinessEmail);
  };

  const handleCallClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (telHref) {
      window.location.href = telHref;
    }
  };

  const handleStarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    if (onRemove) {
      onRemove(provider.BusinessId, provider.BusinessName);
      return;
    }

    onToggleSaved?.(provider.BusinessId);
  };

  const handleClose = () => setShowAuthDialog(false);

  const handleCardClick = () => {
    if (onClick) onClick();
  };

  return (
    <>
      <StyledCard onClick={handleCardClick}>
        <StarContainer onClick={handleStarClick}>
          {isSaved ? (
            <StarIcon color="primary" />
          ) : (
            <StarBorderIcon color="action" />
          )}
        </StarContainer>

        <StyledCardContent>
          {/* Header: name, rating, location, contact + photo */}
          <BusinessHeader>
            <BusinessInfo>
              <BusinessName variant="h6">{provider.BusinessName}</BusinessName>

              <RatingsStack>
                <RatingRow>
                  {hasReviews ? (
                    <CompactBusinessRatingLine
                      source="nky"
                      rating={provider.AverageRating}
                      reviewCount={reviewCountNum}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No Reviews Yet
                    </Typography>
                  )}
                </RatingRow>

                {hasGoogleReview && (
                  <CompactBusinessRatingLine
                    source="google"
                    rating={googleReview.Rating}
                    reviewCount={googleReviewCount}
                  />
                )}
              </RatingsStack>

              <LocationInfo>
                <BusinessIcon />
                <Typography variant="body2">{resolvedLocation}</Typography>
              </LocationInfo>

              {provider.BusinessEmail && (
                <EmailInfo>
                  <EmailIcon />
                  <Typography variant="body2">
                    {provider.BusinessEmail}
                  </Typography>
                </EmailInfo>
              )}

              {formattedPhone && (
                <EmailInfo>
                  <PhoneIcon />
                  <Typography variant="body2">{formattedPhone}</Typography>
                </EmailInfo>
              )}
            </BusinessInfo>

            {provider.BusinessPhotoUrl && (
              <BusinessPhoto>
                <img
                  src={provider.BusinessPhotoUrl}
                  alt={provider.BusinessName}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </BusinessPhoto>
            )}
          </BusinessHeader>

          <StyledDivider />

          {/* Trades */}
          <ServicesTitle variant="subtitle2">
            <LocalOfferIcon /> Trades Offered
          </ServicesTitle>

          {tradeLabels.length > 0 ? (
            <ServicesList>
              {tradeLabels.map((label) => (
                <ServiceItem key={label} variant="body2">
                  {label}
                </ServiceItem>
              ))}
            </ServicesList>
          ) : (
            <ServiceItem variant="body2" className="empty">
              No trades listed yet.
            </ServiceItem>
          )}

          {/* Contact + Call CTAs */}
          <ContactActionsRow>
            {provider.BusinessEmail && (
              <ContactBox
                onClick={handleContactClick}
                role="button"
                tabIndex={0}
                aria-label={`Email ${provider.BusinessName}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleContactClick(e);
                }}
              >
                <EmailIcon />
                <Typography variant="button">Contact</Typography>
              </ContactBox>
            )}
            {telHref && (
              <CallBox
                onClick={handleCallClick}
                role="button"
                tabIndex={0}
                aria-label={`Call ${provider.BusinessName}${formattedPhone ? ` at ${formattedPhone}` : ''}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleCallClick(e);
                }}
              >
                <PhoneIcon />
                <Typography variant="button">Call</Typography>
              </CallBox>
            )}
          </ContactActionsRow>
        </StyledCardContent>
      </StyledCard>

      <AuthModal
        open={showAuthDialog}
        onClose={handleClose}
        message="Saving businesses is a feature for users that have accounts. Would you like to create an account or log in?"
      />
    </>
  );
};

export default ProviderCard;
